import { SITE, absoluteUrl } from "@/lib/site";

export type BreadcrumbItem = {
  name: string;
  path: string;
};

/** Home > … trail for inner pages. */
export function breadcrumbsFor(pageName: string, pagePath: string): BreadcrumbItem[] {
  return [
    { name: "Home", path: "/" },
    { name: pageName, path: pagePath },
  ];
}

export type FaqItem = {
  question: string;
  answer: string;
};

export function breadcrumbListJsonLd(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function faqPageJsonLd(faqs: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

/** Canonical LocalBusiness entity — same @id, name, phone, and URL everywhere. */
export function localBusinessEntity() {
  return {
    "@type": "LocalBusiness" as const,
    "@id": `${SITE.baseUrl}/#business`,
    name: SITE.name,
    description:
      "New Jersey based trucking company hauling produce, dry goods and general freight coast to coast since 2012.",
    url: `${SITE.baseUrl}/`,
    telephone: SITE.phoneE164,
    email: SITE.email,
    foundingDate: SITE.foundingYear,
    image: `${SITE.baseUrl}/apple-touch-icon.png`,
    logo: `${SITE.baseUrl}/apple-touch-icon.png`,
    address: { "@type": "PostalAddress", addressRegion: "NJ", addressCountry: "US" },
    areaServed: "United States",
    sameAs: [SITE.facebookUrl],
    openingHours: "Mo-Su 00:00-23:59",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: SITE.phoneE164,
      contactType: "customer service",
      availableLanguage: "English",
      areaServed: "US",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Freight Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Fresh produce hauling" },
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Dry goods transport" },
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "General freight (FTL dry van)" },
        },
        {
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: "Temperature controlled reefer" },
        },
      ],
    },
  };
}

/** Sitewide LocalBusiness + WebSite graph (homepage). */
export function localBusinessGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      localBusinessEntity(),
      {
        "@type": "WebSite",
        "@id": `${SITE.baseUrl}/#website`,
        url: `${SITE.baseUrl}/`,
        name: SITE.name,
        publisher: { "@id": `${SITE.baseUrl}/#business` },
      },
    ],
  };
}

/**
 * Service JSON-LD. provider references LocalBusiness `#business`
 * (emitted on the homepage and on every InnerPage).
 */
export function serviceJsonLd(input: {
  name: string;
  description: string;
  path: string;
  serviceType: string;
  idSuffix: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.baseUrl}${input.path}#${input.idSuffix}`,
    name: input.name,
    description: input.description,
    serviceType: input.serviceType,
    url: absoluteUrl(input.path),
    provider: { "@id": `${SITE.baseUrl}/#business` },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
  };
}

export function jsonLdScript(data: unknown) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify(data),
  };
}
